/**
 * A thin request helper for the notification and never-miss endpoints.
 *
 * TEMPORARY, and deliberately small. `src/api/client.ts` is the one place a
 * bearer token is attached, and these calls belong on its `api` object — but
 * that file is owned by another stream of work right now, so folding them in
 * would collide. The wiring note that ships with this module contains the
 * exact `api.notifications` / `api.dashboard.needsAttention` block to paste
 * in; once it lands, delete this file and repoint the two hooks.
 *
 * It reuses `ApiError` rather than defining a second error type, so
 * `ErrorState` and every `catch` in the app keep working unchanged.
 */
import type {
  CoachingPage,
  CoachingQuery,
  ListNotificationsQuery,
  MarkReadResponse,
  NeedsAttentionQuery,
  NeedsAttentionResponse,
  NotificationPage,
  UnreadCount,
} from '@crm/contracts';
import { apiFetch, qs } from './http';

/** Booleans are spelled out rather than serialised: `unreadOnly=false` would read as a filter. */

export const notificationsApi = {
  list: (q: ListNotificationsQuery) =>
    apiFetch<NotificationPage>(
      `/api/notifications${qs({ limit: q.limit, offset: q.offset, unreadOnly: q.unreadOnly })}`,
    ),
  unreadCount: () => apiFetch<UnreadCount>('/api/notifications/unread-count'),
  markRead: (id: number) => apiFetch<MarkReadResponse>(`/api/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: () => apiFetch<MarkReadResponse>('/api/notifications/read-all', { method: 'POST' }),
};

/** §8's AI Coach: analysed calls and what to do differently next time. */
export const coachingApi = {
  list: (q: CoachingQuery) =>
    apiFetch<CoachingPage>(
      `/api/coaching${qs({
        ownerId: q.ownerId,
        from: q.from,
        to: q.to,
        maxScore: q.maxScore,
        limit: q.limit,
        offset: q.offset,
      })}`,
    ),
};

export const needsAttentionApi = {
  get: (q: NeedsAttentionQuery) =>
    apiFetch<NeedsAttentionResponse>(
      `/api/dashboard/needs-attention${qs({
        staleDays: q.staleDays,
        mineOnly: q.mineOnly,
        ownerId: q.ownerId,
        limit: q.limit,
      })}`,
    ),
};
